import { Box } from '@mui/material'
import ArrowNext from 'assets/ArrowNext'
import CalendarIcon from 'assets/CalendarIcon'
import moment, { Moment } from 'moment'
import { useEffect, useRef, useState } from 'react'
import { COLORS } from 'styles/colors'
import { TypographySecondary } from 'ui/typography/TypographySecondary'
import Calendar from 'ui/DatePickerInput/components/Calendar'
import Portal from 'ui/DatePickerInput/components/Portal'
import { DropdownContainer } from 'ui/DatePickerInput/DateRangePicker/DateRangePicker.styles'
import { useDropdownPosition } from 'ui/DatePickerInput/hooks'

type FeedingDateFieldProps = {
	startDate: Moment
	endDate: Moment
	onChange: (start: Moment, end: Moment) => void
}

const formatDayLabel = (date: Moment) => {
	const formatted = date.format('DD.MM.YY')
	if (date.isSame(moment().subtract(1, 'day'), 'day')) {
		return `Вчера, ${formatted}`
	}
	return formatted
}

const formatRangeLabel = (start: Moment, end: Moment) => {
	if (start.isSame(end, 'day')) return formatDayLabel(start)
	return `${start.format('DD.MM.YY')} — ${end.format('DD.MM.YY')}`
}

const isFutureDate = (date: Moment) =>
	date.isAfter(moment().subtract(1, 'day'), 'day')

type DateDraft = [Moment | null, Moment | null]

const FeedingDateField = ({
	startDate,
	endDate,
	onChange,
}: FeedingDateFieldProps) => {
	const [isOpen, setIsOpen] = useState(false)
	const [isHovered, setIsHovered] = useState(false)
	const [draftRange, setDraftRange] = useState<DateDraft>([startDate, endDate])
	const inputRef = useRef<HTMLDivElement>(null)
	const dropdownRef = useRef<HTMLDivElement>(null)
	const position = useDropdownPosition(isOpen, inputRef, dropdownRef)
	const yesterday = moment().subtract(1, 'day').startOf('day')
	const isCustom =
		!startDate.isSame(yesterday, 'day') || !endDate.isSame(yesterday, 'day')
	const accent = isHovered || isOpen || isCustom

	const resetToYesterday = () => {
		const day = moment().subtract(1, 'day').startOf('day')
		onChange(day, day.clone())
		setIsOpen(false)
	}

	useEffect(() => {
		if (!isOpen) return

		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as Node
			if (
				inputRef.current?.contains(target) ||
				dropdownRef.current?.contains(target)
			) {
				return
			}
			setIsOpen(false)
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [isOpen])

	return (
		<>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
				{isCustom && (
					<TypographySecondary
						sx={{ cursor: 'pointer' }}
						onClick={resetToYesterday}
					>
						Сбросить
					</TypographySecondary>
				)}
				<Box
					ref={inputRef}
					onClick={() => {
						setDraftRange([startDate.clone(), endDate.clone()])
						setIsOpen(open => !open)
					}}
					onMouseEnter={() => setIsHovered(true)}
					onMouseLeave={() => setIsHovered(false)}
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						flexShrink: 0,
						minWidth: '210px',
						width: 'max-content',
						height: '40px',
						px: '12px',
						gap: '8px',
						borderRadius: '12px',
						border: `1px solid ${accent ? COLORS.main : COLORS.inputBorder}`,
						backgroundColor: COLORS.white,
						cursor: 'pointer',
						userSelect: 'none',
						transition: 'border-color 0.2s ease',
					}}
				>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: '8px',
							minWidth: 0,
						}}
					>
						<CalendarIcon
							width={20}
							height={20}
							color={accent ? COLORS.main : COLORS.icon}
						/>
						<Box
							component='span'
							sx={{
								fontSize: '14px',
								fontWeight: 600,
								lineHeight: '17px',
								fontFamily: 'Montserrat',
								color: accent ? COLORS.main : COLORS.icon,
								whiteSpace: 'nowrap',
							}}
						>
							{formatRangeLabel(startDate, endDate)}
						</Box>
					</Box>
					<ArrowNext
						width={20}
						height={20}
						color={accent ? COLORS.main : COLORS.icon}
						direction={isOpen ? 'up' : 'down'}
					/>
				</Box>
			</Box>
			<Portal>
				<DropdownContainer
					ref={dropdownRef}
					$isOpen={isOpen}
					$top={position.top}
					$left={position.left}
				>
					<Calendar
						currentDate={draftRange[0] || startDate}
						selectedRange={draftRange}
						mode='range'
						onSelectRange={range => {
							const [start, end] = range
							if (!start || isFutureDate(start)) return
							if (!end) {
								setDraftRange([start.clone().startOf('day'), null])
								return
							}
							if (isFutureDate(end)) return
							const nextStart = start.clone().startOf('day')
							const nextEnd = end.clone().startOf('day')
							setDraftRange([nextStart, nextEnd])
							onChange(nextStart, nextEnd)
							setIsOpen(false)
						}}
						isDateDisabled={isFutureDate}
					/>
				</DropdownContainer>
			</Portal>
		</>
	)
}

export default FeedingDateField
